import { PatternRule, PageDefinition, Term, Domain, Query } from '@fullstory/proto/fs/api/pages';
import { NOT_DEFINED_LABEL } from './constants';
import { assertExhaustive } from '@fullstory/typeutils';

type PageDef = Pick<PageDefinition, 'name' | 'labels' | 'patterns'>;

export const getPageName = (page: PageDef | undefined) => {
  if (!page) return NOT_DEFINED_LABEL;
  const name = page.name;
  if (name) return page.name;
  // if the name is empty, first check if this is an api defined page
  if (page.labels.length) {
    return page.labels[0]!.pageName;
  }
  // fall back to the url pattern.
  return renderedPageDefPattern(page);
};

export const renderedPageDefPattern = (page: PageDef) =>
  domainsToString(page.patterns[0]?.domains) + pathFromPatternRule(page.patterns[0] ?? null);

export const renderedPageDefPatternRule = (pattern: PatternRule) =>
  domainsToString(pattern.domains) + pathFromPatternRule(pattern ?? null);

export const domainsToString = (domains: Domain[] | undefined): string => {
  if (!domains || !domains.length) return '';
  const strs = domains.map(domain => domain.terms.map(t => termToString(t) ?? '').join('.'));
  const firstString = strs[0];
  if (firstString) {
    return firstString;
  }
  return `[${strs.join(', ')}]`;
};

export const pathFromPatternRule = (pattern: PatternRule | null): string => {
  if (!pattern) return '';
  const query = pattern.query.length
    ? `?${queryToString(pattern.query)
        .map(q => `${q.key}=${q.value}`)
        .join('&')}`
    : '';
  const fragment = pattern.fragment.length ? `#${termsToString(pattern.fragment, false)}` : '';
  return termsToString(pattern.path) + query + fragment;
};

export const termsToString = (terms: Term[], leading: boolean = true): string => {
  const separator = '/';
  if (!terms.length) return leading ? separator : '';

  const path: Array<string> = [];

  terms.forEach(term => {
    const str = termToString(term);
    if (str !== undefined) {
      path.push(str);
    }
  });

  const pathString = path.join(separator);
  if (leading) return `${separator}${pathString}`;

  return pathString;
};

export const queryToString = (query: Query[]): { key: string; value: string }[] =>
  query.map(q => ({
    key: q.key,
    value: (q.value ? termToString(q.value) : undefined) ?? '',
  }));

const termToString = (term: Term): string | undefined => {
  const oneof = Term.kind_oneof(term);
  if (!oneof) {
    return undefined;
  }
  switch (oneof.opt) {
    case Term.kind.literal:
      return oneof.val;
    case Term.kind.group:
      return `[${oneof.val.literals.join(', ')}]`;
    case Term.kind.wildcard:
      return oneof.val.double ? '**' : '*';
    default:
      assertExhaustive(oneof);
  }
};
